// Environment-based default relay-protocol orchestrator API base URLs
// (2026-07-30 user decision superseding the original design's "apiBaseUrl is
// required" rule -- see docs/superpowers/specs/2026-07-29-relay-deposit-dapp-ui-design.md
// §13). An explicit `apiBaseUrl` (or a pre-built `client`, checked by the
// callers of this module) still takes precedence; this is only the fallback
// for hosts that pass `environment` instead.
//
// Static `Record<Environment, string>` map, mirroring lib/remote-config.ts's
// naming convention (`RELAY_API_BASE_URLS`, singular-capability lookup) --
// but as a hardcoded map rather than a CDN fetch, since relay's backend origin
// is a fixed set of three known URLs, not remote config that changes without
// a redeploy.
import { resolveEnvironment } from "../../../lib/environment";
import type { Environment } from "../../../types";

export const RELAY_API_BASE_URLS: Record<Environment, string> = {
  dev: "https://dev-relay-protocol-api.onechain.nexus",
  // stage points at the dev orchestrator for now (2026-07-30 user decision):
  // a stage relay backend isn't deployed yet. Flip back to
  // https://stage-relay-protocol-api.onechain.nexus once it exists.
  stage: "https://dev-relay-protocol-api.onechain.nexus",
  production: "https://relay-protocol-api.onechain.nexus",
};

/**
 * Resolves the relay API base URL a client should be built against.
 *
 * Resolution order: an explicit `apiBaseUrl` always wins; otherwise falls
 * back to `RELAY_API_BASE_URLS[resolveEnvironment(environment)]` --
 * `resolveEnvironment` normalizes aliases (e.g. "staging" -> "stage") and,
 * when `environment` itself is omitted, falls back to the global
 * `VITE_CROSSX_ENVIRONMENT` / `NEXT_PUBLIC_CROSSX_ENVIRONMENT` /
 * `CROSSX_ENVIRONMENT` env vars the same way every other dapp-ui feature
 * does (see lib/environment.ts), defaulting to "production" when none of
 * those are set either.
 *
 * Always returns a URL -- there is no "neither provided" failure case any
 * more, unlike the pre-existing `client`-memo throw branches this feeds.
 * `environment` accepts the same alias-tolerant input `resolveEnvironment`
 * does (wider than the public `Environment`-typed component props), since
 * this is an internal resolution helper, not a prop surface.
 */
export function resolveRelayApiBaseUrl(opts: {
  apiBaseUrl?: string;
  environment?: Parameters<typeof resolveEnvironment>[0];
}): string {
  if (opts.apiBaseUrl) return opts.apiBaseUrl;
  return RELAY_API_BASE_URLS[resolveEnvironment(opts.environment)];
}
